import React, { useState, useEffect, useCallback } from 'react';
import ConditionalApprovalSection from './ConditionalApprovalSection';
import {
	ApplicantData,
	JointApplicationData,
	Expenses,
	PersonalInformation,
	Property,
	RequestedFinancialParameters,
	PersonaEnum,
	ProductEnum,
	MaritalStatusEnum,
	ResidentialTypeEnum,
	GenderEnum,
	AdditionalIncomeTypeEnum,
	SectorEnum,
	JointApplicationResponse
} from './types';

// ==================== CONSTANTS ====================
const FORM_OPTIONS = {
	PERSONA: Object.values(PersonaEnum),
	PRODUCT_TYPE: Object.values(ProductEnum),
	MARITAL_STATUS: Object.values(MaritalStatusEnum),
	RESIDENTIAL_TYPE: Object.values(ResidentialTypeEnum),
	GENDER: Object.values(GenderEnum),
	ADDITIONAL_INCOME_TYPE: Object.values(AdditionalIncomeTypeEnum),
	SECTOR: Object.values(SectorEnum)
};

const INITIAL_EXPENSES: Expenses = {
	rent: 0,
	foodAndBeverages: 0,
	education: 0,
	healthCare: 0,
	utilities: 0,
	transportation: 0,
	insurance: 0,
	futureExpenses: 0,
	others: 0
};

const INITIAL_PERSONAL_INFO: PersonalInformation = {
	dateOfBirth: {
		gregorian: '1990-01-01'
	},
	gender: GenderEnum.Male,
	firstName: '',
	familyName: '',
	mobileNumber: '500000000'
};

const createInitialApplicant = (index: number = 0): ApplicantData => ({
	nationalId: '',
	nationality: "SAUDI",
	profileId: `PROFILE_${index.toString().padStart(3, '0')}`,
	sector: SectorEnum.GovernmentCivilian,
	persona: PersonaEnum.SALARIED,
	personalInformation: { ...INITIAL_PERSONAL_INFO },
	maritalStatus: MaritalStatusEnum.Single,
	residentialType: ResidentialTypeEnum.Rented,
	numberOfDependents: 0,
	numberOfDependentsInSchool: 0,
	numberOfCarsOwned: 0,
	expenses: { ...INITIAL_EXPENSES }
});

const INITIAL_PROPERTY: Property = {
	propertyId: 'PROP_001',
	productType: ProductEnum.STANDARD,
	price: 1500000
};

const INITIAL_REQUESTED_FINANCIAL_PARAMETERS: RequestedFinancialParameters = {
	downPayment: 500000,
	tenor: 45,
	includeSubsidy: true,
	isFirstHome: false,
	subsidyAmount: 0
};

// ==================== UTILITY COMPONENTS ====================
const InputField: React.FC<{
	label: string;
	type: string;
	value: any;
	onChange: (value: any) => void;
	options?: string[];
	disabled?: boolean;
	placeholder?: string;
}> = ({ label, type, value, onChange, options, disabled = false, placeholder }) => (
	<div>
		<label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
		{type === 'select' && options ? (
			<select
				value={value}
				onChange={(e) => onChange(e.target.value)}
				disabled={disabled}
				className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 text-sm">
				{options.map((opt) => (
					<option key={opt} value={opt}>
						{opt}
					</option>
				))}
			</select>
		) : (
			<input
				type={type}
				value={value}
				onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
				disabled={disabled}
				placeholder={placeholder}
				className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 text-sm"
			/>
		)}
	</div>
);

const FormSection: React.FC<{ title: string; children: React.ReactNode; onRemove?: () => void }> = ({
	title,
	children,
	onRemove
}) => (
	<div className="bg-white p-4 rounded-lg shadow-sm border">
		<div className="flex items-center justify-between mb-4">
			<h3 className="text-lg font-semibold text-gray-800">{title}</h3>
			{onRemove && (
				<button
					onClick={onRemove}
					className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-1 border border-red-300 rounded hover:bg-red-50 transition-colors">
					Remove
				</button>
			)}
		</div>
		<div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
	</div>
);

// ==================== APPLICANT FORM COMPONENT ====================
interface ApplicantFormProps {
	applicant: ApplicantData;
	onChange: (updatedApplicant: ApplicantData) => void;
	onRemove?: () => void;
	title: string;
}

const ApplicantForm: React.FC<ApplicantFormProps> = ({ applicant, onChange, onRemove, title }) => {
	const updateField = <K extends keyof ApplicantData>(field: K, value: ApplicantData[K]) => {
		onChange({ ...applicant, [field]: value });
	};

	const updatePersonalInfo = <K extends keyof PersonalInformation>(field: K, value: PersonalInformation[K]) => {
		onChange({
			...applicant,
			personalInformation: {
				...applicant.personalInformation,
				[field]: value
			}
		});
	};

	const updateDateOfBirth = (type: 'gregorian' | 'hijri', value: string) => {
		onChange({
			...applicant,
			personalInformation: {
				...applicant.personalInformation,
				dateOfBirth: {
					...applicant.personalInformation.dateOfBirth,
					[type]: value
				}
			}
		});
	};

	const updateExpenses = <K extends keyof Expenses>(field: K, value: number) => {
		onChange({
			...applicant,
			expenses: {
				...applicant.expenses,
				[field]: value
			}
		});
	};

	return (
		<FormSection title={title} onRemove={onRemove}>
			{/* Basic Info */}
			<InputField
				label="National ID"
				type="text"
				value={applicant.nationalId}
				onChange={(v) => updateField('nationalId', v)}
				placeholder="10 digits"
			/>
			<InputField
				label="Profile ID"
				type="text"
				value={applicant.profileId}
				onChange={(v) => updateField('profileId', v)}
			/>

			{/* Personal Information */}
			<InputField
				label="First Name"
				type="text"
				value={applicant.personalInformation.firstName}
				onChange={(v) => updatePersonalInfo('firstName', v)}
			/>
			<InputField
				label="Family Name"
				type="text"
				value={applicant.personalInformation.familyName}
				onChange={(v) => updatePersonalInfo('familyName', v)}
			/>
			<InputField
				label="Date of Birth (Gregorian)"
				type="date"
				value={applicant.personalInformation.dateOfBirth.gregorian}
				onChange={(v) => updateDateOfBirth('gregorian', v)}
			/>
			<InputField
				label="Gender"
				type="select"
				value={applicant.personalInformation.gender}
				onChange={(v) => updatePersonalInfo('gender', v as GenderEnum)}
				options={FORM_OPTIONS.GENDER}
			/>
			<InputField
				label="Mobile Number"
				type="text"
				value={applicant.personalInformation.mobileNumber}
				onChange={(v) => updatePersonalInfo('mobileNumber', v)}
				placeholder="9-15 digits"
			/>
			<InputField
				label="Persona"
				type="select"
				value={applicant.persona}
				onChange={(v) => updateField('persona', v as PersonaEnum)}
				options={FORM_OPTIONS.PERSONA}
			/>
			<InputField
				label="Sector"
				type="select"
				value={applicant.sector}
				onChange={(v) => updateField('sector', v as SectorEnum)}
				options={FORM_OPTIONS.SECTOR}
			/>
			<InputField
				label="Marital Status"
				type="select"
				value={applicant.maritalStatus}
				onChange={(v) => updateField('maritalStatus', v as MaritalStatusEnum)}
				options={FORM_OPTIONS.MARITAL_STATUS}
			/>
			<InputField
				label="Residential Type"
				type="select"
				value={applicant.residentialType}
				onChange={(v) => updateField('residentialType', v as ResidentialTypeEnum)}
				options={FORM_OPTIONS.RESIDENTIAL_TYPE}
			/>
			<InputField
				label="Number of Dependents"
				type="number"
				value={applicant.numberOfDependents}
				onChange={(v) => updateField('numberOfDependents', v)}
			/>
			<InputField
				label="Dependents in School"
				type="number"
				value={applicant.numberOfDependentsInSchool}
				onChange={(v) => updateField('numberOfDependentsInSchool', v)}
			/>
			<InputField
				label="Number of Cars Owned"
				type="number"
				value={applicant.numberOfCarsOwned}
				onChange={(v) => updateField('numberOfCarsOwned', v)}
			/>

			{/* Expenses - Collapsible section */}
			<div className="col-span-2">
				<details className="mt-2">
					<summary className="cursor-pointer font-medium text-gray-700 text-sm mb-3">Monthly Expenses</summary>
					<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
						<InputField label="Rent" type="number" value={applicant.expenses.rent} onChange={(v) => updateExpenses('rent', v)} />
						<InputField
							label="Food & Beverages"
							type="number"
							value={applicant.expenses.foodAndBeverages}
							onChange={(v) => updateExpenses('foodAndBeverages', v)}
						/>
						<InputField
							label="Education"
							type="number"
							value={applicant.expenses.education}
							onChange={(v) => updateExpenses('education', v)}
						/>
						<InputField
							label="Health Care"
							type="number"
							value={applicant.expenses.healthCare}
							onChange={(v) => updateExpenses('healthCare', v)}
						/>
						<InputField
							label="Utilities"
							type="number"
							value={applicant.expenses.utilities}
							onChange={(v) => updateExpenses('utilities', v)}
						/>
						<InputField
							label="Transportation"
							type="number"
							value={applicant.expenses.transportation}
							onChange={(v) => updateExpenses('transportation', v)}
						/>
						<InputField
							label="Insurance"
							type="number"
							value={applicant.expenses.insurance}
							onChange={(v) => updateExpenses('insurance', v)}
						/>
						<InputField
							label="Future Expenses"
							type="number"
							value={applicant.expenses.futureExpenses}
							onChange={(v) => updateExpenses('futureExpenses', v)}
						/>
						<InputField
							label="Others"
							type="number"
							value={applicant.expenses.others}
							onChange={(v) => updateExpenses('others', v)}
						/>
					</div>
				</details>
			</div>

			{/* Additional Income */}
			<div className="col-span-2">
				<div className="mt-2 p-3 border border-gray-200 rounded-lg bg-gray-50">
					<div className="flex items-center gap-3 mb-2">
						<label className="flex items-center gap-2 cursor-pointer">
							<input
								type="checkbox"
								checked={!!applicant.additionalIncome}
								onChange={(e) => {
									if (e.target.checked) {
										onChange({ ...applicant, additionalIncome: { type: AdditionalIncomeTypeEnum.PRIVATE_BUSINESS } });
									} else {
										onChange({ ...applicant, additionalIncome: undefined });
									}
								}}
								className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
							/>
							<span className="text-sm font-medium text-gray-700">Has Additional Income</span>
						</label>
					</div>
					{applicant.additionalIncome && (
						<div className="mt-2">
							<InputField
								label="Additional Income Type"
								type="select"
								value={applicant.additionalIncome.type}
								onChange={(v) => onChange({ ...applicant, additionalIncome: { type: v as AdditionalIncomeTypeEnum } })}
								options={FORM_OPTIONS.ADDITIONAL_INCOME_TYPE}
							/>
						</div>
					)}
				</div>
			</div>
		</FormSection>
	);
};

// ==================== PROPERTY FORM COMPONENT ====================
interface PropertyFormProps {
	property: Property;
	onChange: (property: Property) => void;
}

const PropertyForm: React.FC<PropertyFormProps> = ({ property, onChange }) => {
	const updateField = <K extends keyof Property>(field: K, value: Property[K]) => {
		onChange({ ...property, [field]: value });
	};

	return (
		<div className="bg-white p-4 rounded-lg shadow-sm border">
			<div className="flex items-center justify-between mb-4">
				<h3 className="text-lg font-semibold text-gray-800">Property Information</h3>
			</div>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<InputField
					label="Property ID"
					type="text"
					value={property.propertyId}
					onChange={(v) => updateField('propertyId', v)}
				/>
				<InputField
					label="Product Type"
					type="select"
					value={property.productType}
					onChange={(v) => updateField('productType', v as ProductEnum)}
					options={FORM_OPTIONS.PRODUCT_TYPE}
				/>
				<InputField
					label="Price (SAR)"
					type="number"
					value={property.price}
					onChange={(v) => updateField('price', v)}
				/>
			</div>
		</div>
	);
};

// ==================== REQUESTED FINANCIAL PARAMETERS FORM COMPONENT ====================
interface RequestedFinancialParametersFormProps {
	params: RequestedFinancialParameters;
	onChange: (params: RequestedFinancialParameters) => void;
}

const RequestedFinancialParametersForm: React.FC<RequestedFinancialParametersFormProps> = ({ params, onChange }) => {
	const updateField = <K extends keyof RequestedFinancialParameters>(field: K, value: RequestedFinancialParameters[K]) => {
		onChange({ ...params, [field]: value });
	};

	return (
		<div className="bg-white p-4 rounded-lg shadow-sm border">
			<div className="flex items-center justify-between mb-4">
				<h3 className="text-lg font-semibold text-gray-800">Requested Financial Parameters</h3>
			</div>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<InputField
					label="Down Payment (SAR)"
					type="number"
					value={params.downPayment}
					onChange={(v) => updateField('downPayment', v)}
				/>
				<InputField
					label="Tenor (Months)"
					type="number"
					value={params.tenor}
					onChange={(v) => updateField('tenor', v)}
				/>
				<InputField
					label="Subsidy Amount (SAR)"
					type="number"
					value={params.subsidyAmount}
					onChange={(v) => updateField('subsidyAmount', v)}
				/>
				<div className="flex flex-col gap-3 justify-center">
					<label className="flex items-center gap-2 cursor-pointer">
						<input
							type="checkbox"
							checked={params.includeSubsidy}
							onChange={(e) => updateField('includeSubsidy', e.target.checked)}
							className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
						/>
						<span className="text-sm font-medium text-gray-700">Include Subsidy</span>
					</label>
					<label className="flex items-center gap-2 cursor-pointer">
						<input
							type="checkbox"
							checked={params.isFirstHome}
							onChange={(e) => updateField('isFirstHome', e.target.checked)}
							className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
						/>
						<span className="text-sm font-medium text-gray-700">Is First Home</span>
					</label>
				</div>
			</div>
		</div>
	);
};

// ==================== RESULTS DISPLAY COMPONENT ====================
// Helper Functions
const formatCurrency = (amount: number) => {
	if (amount === undefined || amount === null) return 'SAR 0';
	return new Intl.NumberFormat('en-SA', {
		style: 'currency',
		currency: 'SAR',
		minimumFractionDigits: 0,
		maximumFractionDigits: 2
	}).format(amount);
};

const formatPercentage = (value: number) => {
	if (value === undefined || value === null) return '0%';
	return `${value.toFixed(2)}%`;
};

const formatStageName = (name: string) =>
	name
		.split('_')
		.map((w) => w.charAt(0) + w.slice(1).toLowerCase())
		.join(' ');

const getStatusColor = (status: string) => {
	const colors = {
		PASSED: 'bg-green-500 text-white',
		FAILED: 'bg-red-500 text-white',
		TO_BE_IMPLEMENTED: 'bg-gray-400 text-white'
	};
	return colors[status as keyof typeof colors] || 'bg-gray-400 text-white';
};

const getStatusBadgeColor = (status: string) => {
	const colors = {
		APPROVED: 'bg-green-100 text-green-800',
		REJECTED: 'bg-red-100 text-red-800',
		PENDING: 'bg-yellow-100 text-yellow-800',
		APPROVED_WITH_DEVIATION: 'bg-yellow-100 text-yellow-800'
	};
	return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
};

const BooleanBadge: React.FC<{ value: boolean }> = ({ value }) => (
	<span
		className={`px-2 py-1 rounded text-xs font-bold ${
			value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
		}`}>
		{value ? 'PASS' : 'FAIL'}
	</span>
);

const getProductCodeLabel = (code: string) => {
	const labels: Record<string, string> = {
		CRC: 'Credit Card',
		AQAR: 'Real Estate',
		MTG: 'Mortgage',
		MFL: 'Microfinance Loan',
		PL: 'Personal Loan',
		AL: 'Auto Loan'
	};
	return labels[code] || code;
};

// Display Components
const ApplicantProfileSection: React.FC<{ applicant: any; title: string }> = ({ applicant, title }) => {
	if (!applicant) return null;
	const { personalInformation } = applicant;

	return (
		<div className="bg-gray-50 p-4 rounded-lg border">
			<h4 className="text-sm font-semibold text-gray-700 mb-3">{title}</h4>
			<div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
				<div>
					<span className="text-gray-600">Name:</span>{' '}
					<span className="font-medium">
						{personalInformation.firstName} {personalInformation.familyName}
					</span>
				</div>
				<div>
					<span className="text-gray-600">National ID:</span>{' '}
					<span className="font-medium font-mono">{applicant.nationalId}</span>
				</div>
				<div>
					<span className="text-gray-600">Age:</span> <span className="font-medium">{personalInformation.age} years</span>
				</div>
				<div>
					<span className="text-gray-600">Gender:</span> <span className="font-medium">{personalInformation.gender}</span>
				</div>
				<div>
					<span className="text-gray-600">Marital Status:</span>{' '}
					<span className="font-medium">{personalInformation.maritalStatus}</span>
				</div>
				<div>
					<span className="text-gray-600">Persona:</span> <span className="font-medium">{applicant.persona}</span>
				</div>
				<div>
					<span className="text-gray-600">Residential:</span>{' '}
					<span className="font-medium">{applicant.residentialType}</span>
				</div>
				<div>
					<span className="text-gray-600">Dependents:</span> <span className="font-medium">{applicant.numberOfDependents}</span>
				</div>
				<div>
					<span className="text-gray-600">Children in School:</span>{' '}
					<span className="font-medium">{applicant.numberOfDependentsInSchool}</span>
				</div>
			</div>
		</div>
	);
};

const RACPolicySection: React.FC<{ stage: any }> = ({ stage }) => {
	if (!stage?.outputResult?.data) return null;
	const policyData = Object.values(stage.outputResult.data)[0] as any;
	if (!policyData) return null;

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">RAC Policy Details</h3>
			<div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
				<div>
					<div className="text-xs text-gray-500">Persona / Sector</div>
					<div className="font-semibold">{policyData.persona}</div>
				</div>
				<div>
					<div className="text-xs text-gray-500">Initial Rating</div>
					<div className="font-semibold text-green-600">{policyData.initialRating}</div>
				</div>
				<div>
					<div className="text-xs text-gray-500">Initial Pricing</div>
					<div className="font-semibold">{policyData.initialPricing}%</div>
				</div>
				<div>
					<div className="text-xs text-gray-500">Min Income</div>
					<div className="font-semibold">{formatCurrency(policyData.minIncome)}</div>
				</div>
				<div>
					<div className="text-xs text-gray-500">Age Range</div>
					<div className="font-semibold">
						{policyData.minAge} - {policyData.maxAgeAtMaturity} years
					</div>
				</div>
				<div>
					<div className="text-xs text-gray-500">Max DBR</div>
					<div className="font-semibold">{policyData.maxDbr}%</div>
				</div>
				<div>
					<div className="text-xs text-gray-500">Max LTV</div>
					<div className="font-semibold">{policyData.maxLtv}%</div>
				</div>
				<div>
					<div className="text-xs text-gray-500">Finance Range</div>
					<div className="font-semibold text-xs">
						{formatCurrency(policyData.minFinanceAmount)} - {formatCurrency(policyData.maxFinanceAmount)}
					</div>
				</div>
			</div>
		</div>
	);
};

const CreditBureauDetailedAnalysis: React.FC<{ stage: any; application: any }> = ({ stage, application }) => {
	if (!stage?.outputResult?.data?.creditBureauResult) return null;
	const creditData = stage.outputResult.data.creditBureauResult;

	const getApplicantName = (nationalId: string) => {
		if (application.mainApplicant.nationalId === nationalId) {
			const p = application.mainApplicant.personalInformation;
			return `${p.firstName} ${p.familyName} (Main)`;
		}
		const coApplicant = application.coApplicants.find((a: any) => a.nationalId === nationalId);
		if (coApplicant) {
			const p = coApplicant.personalInformation;
			return `${p.firstName} ${p.familyName} (Co-Applicant)`;
		}
		return nationalId;
	};

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Credit Bureau & RAC Analysis</h3>
			<div className="space-y-6">
				{Object.entries(creditData).map(([nationalId, data]: [string, any]) => (
					<div key={nationalId} className="border rounded-lg p-4 bg-gray-50">
						<h4 className="font-semibold text-gray-800 mb-4">{getApplicantName(nationalId)}</h4>

						{/* Summary Metrics */}
						<div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
							<div className="bg-white p-3 rounded border text-center">
								<div className="text-xs text-gray-500">Credit Score</div>
								<div className="text-xl font-bold text-blue-600">{data.creditBureauSummary?.score || 'N/A'}</div>
							</div>
							<div className="bg-white p-3 rounded border text-center">
								<div className="text-xs text-gray-500">Current DBR</div>
								<div className="text-xl font-bold text-purple-600">{formatPercentage(data.currentDbr)}</div>
							</div>
							<div className="bg-white p-3 rounded border text-center">
								<div className="text-xs text-gray-500">Obligations</div>
								<div className="text-xl font-bold text-orange-600">
									{formatCurrency(data.creditBureauSummary?.obligations || 0)}
								</div>
							</div>
							<div className="bg-white p-3 rounded border text-center">
								<div className="text-xs text-gray-500">Inquiries (12M)</div>
								<div className="text-xl font-bold text-gray-700">{data.creditBureauSummary?.numberOfInquiries || 0}</div>
							</div>
						</div>

						{/* RAC Checks */}
						{data.creditBureauRacResult?.racChecks && (
							<div className="mb-4">
								<h5 className="text-sm font-semibold text-gray-600 mb-2">RAC Logic Checks</h5>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-2">
									{data.creditBureauRacResult.racChecks.map((check: any, idx: number) => (
										<div key={idx} className="flex justify-between items-center p-2 bg-white rounded border text-xs">
											<span className="font-medium text-gray-700 truncate flex-1" title={check.racId}>
												{formatStageName(check.racId)}
											</span>
											<BooleanBadge value={check.pass} />
										</div>
									))}
								</div>
							</div>
						)}

						{/* Bucket Counts */}
						{data.bucketCounts && (
							<div className="mb-4">
								<h5 className="text-sm font-semibold text-gray-600 mb-2">Payment Buckets (Delinquency)</h5>
								<div className="grid grid-cols-2 md:grid-cols-4 gap-2">
									{Object.entries(data.bucketCounts).map(([key, count]: [string, any]) => (
										<div key={key} className="bg-white p-2 rounded border text-xs">
											<div className="text-gray-500 truncate" title={key}>
												{key}
											</div>
											<div className={`text-lg font-bold ${count > 0 && key !== 'BKT_0' ? 'text-red-600' : 'text-gray-700'}`}>
												{count}
											</div>
										</div>
									))}
								</div>
							</div>
						)}

						{/* Credit Instruments */}
						{data.creditReport?.creditInstruments && data.creditReport.creditInstruments.length > 0 && (
							<div>
								<h5 className="text-sm font-semibold text-gray-600 mb-2">
									Credit Instruments ({data.creditReport.creditInstruments.length})
								</h5>
								<div className="space-y-2">
									{data.creditReport.creditInstruments.map((instrument: any, idx: number) => (
										<div key={idx} className="bg-white p-3 rounded border">
											<div className="flex justify-between items-start mb-2">
												<div>
													<span className="font-semibold text-gray-800">{getProductCodeLabel(instrument.productCode)}</span>
													<span
														className={`ml-2 px-2 py-0.5 rounded text-xs ${
															instrument.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
														}`}>
														{instrument.isActive ? 'Active' : 'Closed'}
													</span>
												</div>
												<div className="text-right text-xs">
													<div className="text-gray-500">Outstanding</div>
													<div className="font-bold text-orange-600">{formatCurrency(instrument.outstandingBalance)}</div>
												</div>
											</div>
											<div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
												<div>
													<span className="text-gray-500">Limit:</span>{' '}
													<span className="font-medium">{formatCurrency(instrument.limit)}</span>
												</div>
												<div>
													<span className="text-gray-500">Installment:</span>{' '}
													<span className="font-medium">{formatCurrency(instrument.installmentAmount)}</span>
												</div>
												<div>
													<span className="text-gray-500">Frequency:</span>{' '}
													<span className="font-medium">{instrument.paymentFrequencyCode}</span>
												</div>
												<div>
													<span className="text-gray-500">History:</span>{' '}
													<span className="font-mono text-[10px]">{instrument.paymentHistoryCodes}</span>
												</div>
											</div>
										</div>
									))}
								</div>
							</div>
						)}

						{/* Bounced Cheques */}
						{data.creditReport?.bouncedCheques && data.creditReport.bouncedCheques.length > 0 && (
							<div className="mt-4">
								<h5 className="text-sm font-semibold text-red-600 mb-2">
									Bounced Cheques ({data.creditReport.bouncedCheques.length})
								</h5>
								<div className="space-y-2">
									{data.creditReport.bouncedCheques.map((cheque: any, idx: number) => (
										<div key={idx} className="bg-red-50 p-3 rounded border border-red-200">
											<div className="flex justify-between items-start mb-2">
												<span className="font-semibold text-gray-800">Cheque #{cheque.chequeId}</span>
												<span className="px-2 py-0.5 rounded text-xs bg-gray-200 text-gray-800">{cheque.status}</span>
											</div>
											<div className="grid grid-cols-2 gap-2 text-xs">
												<div>
													Amount: <span className="font-bold">{formatCurrency(cheque.amount)}</span>
												</div>
												<div>Date: {new Date(cheque.eventDate).toLocaleDateString()}</div>
												{cheque.settlementDate && (
													<div>Settled: {new Date(cheque.settlementDate).toLocaleDateString()}</div>
												)}
											</div>
										</div>
									))}
								</div>
							</div>
						)}

						{/* Execution Judgments */}
						{data.creditReport?.executionJudgments && data.creditReport.executionJudgments.length > 0 && (
							<div className="mt-4">
								<h5 className="text-sm font-semibold text-red-600 mb-2">
									Execution Judgments ({data.creditReport.executionJudgments.length})
								</h5>
								<div className="space-y-2">
									{data.creditReport.executionJudgments.map((judgment: any, idx: number) => (
										<div key={idx} className="bg-red-50 p-3 rounded border border-red-200">
											<div className="flex justify-between items-start mb-2">
												<span className="font-semibold text-gray-800">Judgment</span>
												<span className="px-2 py-0.5 rounded text-xs bg-gray-200 text-gray-800">{judgment.status}</span>
											</div>
											<div className="grid grid-cols-2 gap-2 text-xs">
												<div>
													Amount: <span className="font-bold">{formatCurrency(judgment.amount)}</span>
												</div>
												<div>Filing Date: {new Date(judgment.filingDate).toLocaleDateString()}</div>
												{judgment.settlementDate && (
													<div>Settled: {new Date(judgment.settlementDate).toLocaleDateString()}</div>
												)}
											</div>
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				))}
			</div>
		</div>
	);
};

const OpenBankingSection: React.FC<{ stage: any; application: any }> = ({ stage, application }) => {
	const results = stage?.outputResult?.data?.openBankingResults;
	if (!results) return null;

	const getApplicantName = (nationalId: string) => {
		if (application.mainApplicant.nationalId === nationalId) {
			const p = application.mainApplicant.personalInformation;
			return `${p.firstName} ${p.familyName} (Main)`;
		}
		const coApplicant = application.coApplicants.find((a: any) => a.nationalId === nationalId);
		if (coApplicant) {
			const p = coApplicant.personalInformation;
			return `${p.firstName} ${p.familyName} (Co-Applicant)`;
		}
		return nationalId;
	};

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Open Banking Analysis</h3>
			<div className="space-y-4">
				{Object.entries(results).map(([nationalId, data]: [string, any]) => (
					<div key={nationalId} className="border rounded-lg p-4 bg-gray-50">
						<h4 className="font-semibold text-gray-800 mb-3">{getApplicantName(nationalId)}</h4>

						<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-3 text-sm">
							<div className="bg-white p-3 rounded border text-center">
								<div className="text-xs text-gray-500">Income (Derived)</div>
								<div className="text-lg font-bold text-green-700">{formatCurrency(data.income)}</div>
							</div>
							<div className="bg-white p-3 rounded border text-center">
								<div className="text-xs text-gray-500">Avg Credit Amount</div>
								<div className="text-lg font-bold text-blue-700">{formatCurrency(data.avgCreditAmount)}</div>
							</div>
							<div className="bg-white p-3 rounded border text-center">
								<div className="text-xs text-gray-500">Avg Credit Count</div>
								<div className="text-lg font-bold text-purple-700">{data.avgCreditCount}</div>
							</div>
							<div className="bg-white p-3 rounded border text-center">
								<div className="text-xs text-gray-500">Avg Debit Amount</div>
								<div className="text-lg font-bold text-orange-700">{formatCurrency(data.avgDebitAmount)}</div>
							</div>
							<div className="bg-white p-3 rounded border text-center">
								<div className="text-xs text-gray-500">Avg Closing Balance</div>
								<div className="text-lg font-bold text-gray-800">{formatCurrency(data.avgClosingBalance)}</div>
							</div>
						</div>

						{data.indirectDebits && Array.isArray(data.indirectDebits) && (
							<div className="mb-3 text-xs text-gray-700">
								<span className="font-semibold">Indirect Debits:</span>{' '}
								<span>{data.indirectDebits.length}</span>

								{data.indirectDebits.length > 0 && (
									<div className="overflow-x-auto mt-2">
										<table className="min-w-full text-xs text-left bg-white rounded border">
											<thead className="bg-gray-100 text-gray-600 uppercase font-medium">
												<tr>
													<th className="p-2 border-b">Amount</th>
													<th className="p-2 border-b">Transaction ID</th>
													<th className="p-2 border-b">Date</th>
												</tr>
											</thead>
											<tbody>
												{data.indirectDebits.map((txn: any, idx: number) => (
													<tr key={txn.transactionId || idx} className="border-b">
														<td className="p-2">{formatCurrency(txn.amount)}</td>
														<td className="p-2 font-mono text-[11px]">{txn.transactionId}</td>
														<td className="p-2">{txn.date ? new Date(txn.date).toLocaleString() : ''}</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								)}
							</div>
						)}

						{data.extraDetails && (
							<div className="mt-2 text-xs">
								<details className="bg-white rounded border p-2">
									<summary className="cursor-pointer font-medium text-gray-700">
										Ignored Transactions Details
									</summary>
									<div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
										<div className="bg-gray-50 p-2 rounded border">
											<div className="text-gray-500 mb-1 text-[11px]">Ignored Credit Transactions</div>
											<div className="font-mono text-[11px] overflow-x-auto">
												{(data.extraDetails.ignoredCreditTransactions || []).length === 0
													? 'None'
													: JSON.stringify(data.extraDetails.ignoredCreditTransactions, null, 2)}
											</div>
										</div>
										<div className="bg-gray-50 p-2 rounded border">
											<div className="text-gray-500 mb-1 text-[11px]">Ignored Debit Transactions</div>
											<div className="font-mono text-[11px] overflow-x-auto">
												{(data.extraDetails.ignoredDebitTransactions || []).length === 0
													? 'None'
													: JSON.stringify(data.extraDetails.ignoredDebitTransactions, null, 2)}
											</div>
										</div>
									</div>
								</details>
							</div>
						)}
					</div>
				))}
			</div>
		</div>
	);
};

const NonSalariedRacSection: React.FC<{ stage: any; application: any }> = ({ stage, application }) => {
	const data = stage?.outputResult?.data;
	if (!data) return null;

	const results = data.nonSalariedRacsResult || {};
	const config = data.nonSalariedRac;

	const getApplicantName = (nationalId: string) => {
		if (application.mainApplicant.nationalId === nationalId) {
			const p = application.mainApplicant.personalInformation;
			return `${p.firstName} ${p.familyName} (Main)`;
		}
		const coApplicant = application.coApplicants.find((a: any) => a.nationalId === nationalId);
		if (coApplicant) {
			const p = coApplicant.personalInformation;
			return `${p.firstName} ${p.familyName} (Co-Applicant)`;
		}
		return nationalId;
	};

	const getStatusClasses = (status: string) => {
		if (status === 'PASS' || status === 'PASSED') {
			return 'bg-green-100 text-green-800';
		}
		if (status === 'FAIL' || status === 'FAILED') {
			return 'bg-red-100 text-red-800';
		}
		return 'bg-gray-100 text-gray-800';
	};

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Non-Salaried RAC Evaluation</h3>
			
			<div className="space-y-6">
				{/* Per-applicant RAC evaluation */}
				{Object.entries(results).map(([nationalId, result]: [string, any]) => (
					<div key={nationalId} className="border rounded-lg p-4 bg-gray-50">
						<div className="flex items-center justify-between mb-3">
							<h4 className="font-semibold text-gray-800">{getApplicantName(nationalId)}</h4>
							<span
								className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusClasses(
									result.status
								)}`}>
								Status: {result.status}
							</span>
						</div>

						{Array.isArray(result.evaluationDetails) && result.evaluationDetails.length > 0 && (
							<div className="overflow-x-auto">
								<table className="min-w-full text-xs text-left bg-white rounded border">
									<thead className="bg-gray-100 text-gray-600 uppercase font-medium">
										<tr>
											<th className="p-2 border-b">Rule</th>
											<th className="p-2 border-b">Value</th>
											<th className="p-2 border-b">Threshold</th>
											<th className="p-2 border-b">Type</th>
											<th className="p-2 border-b">Unit</th>
											<th className="p-2 border-b">Classification</th>
											<th className="p-2 border-b text-center">Status</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-gray-200">
										{result.evaluationDetails.map((detail: any, idx: number) => (
											<tr key={idx} className="hover:bg-gray-50">
												<td className="p-2 font-medium text-gray-800">
													{formatStageName(detail.rule || '')}
												</td>
												<td className="p-2 text-gray-700 font-mono">
													{detail.value !== undefined ? detail.value.toLocaleString() : '-'}
												</td>
												<td className="p-2 text-gray-700 font-mono">
													{detail.racThreshold !== undefined
														? detail.racThreshold.toLocaleString()
														: '-'}
												</td>
												<td className="p-2 text-gray-600">{detail.thresholdType || '-'}</td>
												<td className="p-2 text-gray-600">{detail.unit || '-'}</td>
												<td className="p-2 text-gray-600">{detail.classification || '-'}</td>
												<td className="p-2 text-center">
													<span
														className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${getStatusClasses(
															detail.status
														)}`}>
														{detail.status}
													</span>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</div>
				))}

				{/* RAC configuration details */}
				{config && (
					<div className="border rounded-lg p-4 bg-gray-50">
						<div className="flex flex-wrap items-center justify-between mb-3 gap-2">
							<h4 className="font-semibold text-gray-800">RAC Rules Configuration</h4>
							<div className="flex flex-wrap items-center gap-2 text-[11px]">
								{config.version !== undefined && (
									<span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800 font-semibold">
										Version {config.version}
									</span>
								)}
								{config.status && (
									<span className="px-2 py-1 rounded-full bg-gray-100 text-gray-800 font-semibold">
										Status: {config.status}
									</span>
								)}
								{config.effectiveFrom && (
									<span className="px-2 py-1 rounded-full bg-green-100 text-green-800 font-semibold">
										Effective From: {new Date(config.effectiveFrom).toLocaleDateString()}
									</span>
								)}
							</div>
						</div>

						{config.createdAt && (
							<div className="text-[11px] text-gray-600 mb-3">
								Created {new Date(config.createdAt).toLocaleString()} by {config.createdBy || 'N/A'}
							</div>
						)}

						{config.rules && (
							<div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
								{Object.entries(config.rules).map(([ruleName, rule]: [string, any]) => (
									<div key={ruleName} className="bg-white p-3 rounded border">
										<h5 className="font-semibold text-gray-800 mb-1">
											{formatStageName(ruleName)}
										</h5>
										<div className="mb-2 text-gray-700 space-y-1">
											<div>
												<span className="text-gray-500">Type:</span>{' '}
												<span className="font-medium">{rule.type}</span>
											</div>
											<div>
												<span className="text-gray-500">Threshold:</span>{' '}
												<span className="font-medium">
													{rule.threshold !== undefined
														? rule.threshold.toLocaleString()
														: '-'}
												</span>
											</div>
											<div>
												<span className="text-gray-500">Unit:</span>{' '}
												<span className="font-medium">{rule.unit}</span>
											</div>
										</div>

										{Array.isArray(rule.classificationBands) &&
											rule.classificationBands.length > 0 && (
												<div className="mt-2">
													<div className="text-[11px] text-gray-500 mb-1">
														Classification Bands
													</div>
													<div className="max-h-40 overflow-y-auto border rounded">
														<table className="min-w-full text-[11px]">
															<thead className="bg-gray-100 text-gray-600">
																<tr>
																	<th className="px-2 py-1 border-b">Classification</th>
																	<th className="px-2 py-1 border-b text-right">Min</th>
																	<th className="px-2 py-1 border-b text-right">Max</th>
																</tr>
															</thead>
															<tbody className="divide-y divide-gray-200">
																{rule.classificationBands.map(
																	(band: any, idx: number) => (
																		<tr key={idx}>
																			<td className="px-2 py-1">
																				{band.classification}
																			</td>
																			<td className="px-2 py-1 text-right font-mono">
																				{band.min !== undefined
																					? band.min.toLocaleString()
																					: '-'}
																			</td>
																			<td className="px-2 py-1 text-right font-mono">
																				{band.max !== undefined
																					? band.max.toLocaleString()
																					: '-'}
																			</td>
																		</tr>
																	)
																)}
															</tbody>
														</table>
													</div>
												</div>
											)}
									</div>
								))}
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

const ResponsibleLendingSection: React.FC<{ stage: any; application: any }> = ({ stage, application }) => {
	if (!stage?.outputResult?.data) return null;
	const rlData = stage.outputResult.data;

	const getApplicantName = (nationalId: string) => {
		if (application.mainApplicant.nationalId === nationalId) {
			const p = application.mainApplicant.personalInformation;
			return `${p.firstName} ${p.familyName} (Main)`;
		}
		const coApplicant = application.coApplicants.find((a: any) => a.nationalId === nationalId);
		if (coApplicant) {
			const p = coApplicant.personalInformation;
			return `${p.firstName} ${p.familyName} (Co-Applicant)`;
		}
		return nationalId;
	};

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Responsible Lending (Affordability)</h3>
			<div className="space-y-4">
				{Object.entries(rlData)
					.filter(([key]) => key !== 'undefined' && !key.startsWith('user') && !key.startsWith('total'))
					.map(([nationalId, data]: [string, any]) => {
						if (!data.responsibleLendingResult) return null;
						const rl = data.responsibleLendingResult;

						return (
							<div key={nationalId} className="border rounded-lg p-4 bg-gray-50">
								<h4 className="font-semibold text-gray-800 mb-3">{getApplicantName(nationalId)}</h4>

								{/* Summary Cards */}
								<div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
									<div className="bg-blue-50 p-3 rounded text-center">
										<div className="text-xs text-gray-600">Disposable Income</div>
										<div className="text-lg font-bold text-blue-700">{formatCurrency(rl.disposableIncome)}</div>
										<div className="text-xs text-blue-500">{formatPercentage(rl.disposableIncomePercentage)}</div>
									</div>
									<div className="bg-orange-50 p-3 rounded text-center">
										<div className="text-xs text-gray-600">Total Expenses</div>
										<div className="text-lg font-bold text-orange-700">{formatCurrency(rl.totalPersonalExpenditure)}</div>
										<div className="text-xs text-orange-500">{formatPercentage(rl.percentageOfIncome)} of Income</div>
									</div>
									<div className="bg-gray-100 p-3 rounded text-center">
										<div className="text-xs text-gray-600">Income (from input)</div>
										<div className="text-lg font-bold text-gray-700">
											{formatCurrency(data.responsibleLendingInput?.income || 0)}
										</div>
									</div>
								</div>

								{/* Expense Breakdown */}
								<h5 className="text-xs font-semibold text-gray-600 mb-2 uppercase">Calculated Expense Breakdown</h5>
								<div className="grid grid-cols-2 md:grid-cols-4 gap-2">
									{[
										'foodAndBeverages',
										'utilities',
										'transportation',
										'healthcare',
										'rent',
										'education',
										'insurance',
										'others',
										'futureExpenses'
									].map((key) => (
										<div key={key} className="bg-white p-2 rounded border text-xs">
											<div className="text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
											<div className="font-semibold text-gray-800">{formatCurrency(rl[key] || 0)}</div>
										</div>
									))}
								</div>
							</div>
						);
					})}
			</div>
		</div>
	);
};

const MaximumBorrowingPowerSection: React.FC<{ stage: any; application: any }> = ({ stage, application }) => {
	if (!stage?.outputResult?.data?.maximumBorrowingPowerResult) return null;
	const mbpData = stage.outputResult.data.maximumBorrowingPowerResult;
	const jointData = stage.outputResult.data.jointMaximumBorrowingPower;

	const getApplicantName = (nationalId: string) => {
		if (application.mainApplicant.nationalId === nationalId) {
			const p = application.mainApplicant.personalInformation;
			return `${p.firstName} ${p.familyName} (Main)`;
		}
		const coApplicant = application.coApplicants.find((a: any) => a.nationalId === nationalId);
		if (coApplicant) {
			const p = coApplicant.personalInformation;
			return `${p.firstName} ${p.familyName} (Co-Applicant)`;
		}
		return nationalId;
	};

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Maximum Borrowing Power</h3>

			{/* Individual Capacities */}
			<div className="space-y-4 mb-6">
				{Object.entries(mbpData).map(([nationalId, data]: [string, any]) => (
					<div key={nationalId} className="border rounded-lg p-4 bg-gray-50">
						<h4 className="font-semibold text-gray-800 mb-3">{getApplicantName(nationalId)}</h4>
						<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
							<div className="bg-white p-3 rounded border text-center">
								<div className="text-xs text-gray-500">Max Property</div>
								<div className="text-lg font-bold text-blue-600">{formatCurrency(data.maxPropertyPrice)}</div>
							</div>
							<div className="bg-white p-3 rounded border text-center">
								<div className="text-xs text-gray-500">Max Borrowing</div>
								<div className="text-lg font-bold text-green-600">{formatCurrency(data.maxBorrowingAmount)}</div>
							</div>
							<div className="bg-white p-3 rounded border text-center">
								<div className="text-xs text-gray-500">Max EMI</div>
								<div className="text-lg font-bold text-purple-600">{formatCurrency(data.maxEmi)}</div>
							</div>
							<div className="bg-white p-3 rounded border text-center">
								<div className="text-xs text-gray-500">Max Tenor</div>
								<div className="text-lg font-bold text-orange-600">{data.maxTenorMonths} months</div>
							</div>
						</div>
						{data.calculationDetails && (
							<div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
								<div className="bg-white p-2 rounded border">
									<div className="text-gray-500">Current DBR</div>
									<div className="font-semibold">{formatPercentage(data.calculationDetails.currentDbr * 100)}</div>
								</div>
								<div className="bg-white p-2 rounded border">
									<div className="text-gray-500">New DBR</div>
									<div className="font-semibold">{formatPercentage(data.calculationDetails.newDbr * 100)}</div>
								</div>
								<div className="bg-white p-2 rounded border">
									<div className="text-gray-500">Max DBR</div>
									<div className="font-semibold">{formatPercentage(data.calculationDetails.newMaxDbr * 100)}</div>
								</div>
								<div className="bg-white p-2 rounded border">
									<div className="text-gray-500">Min Down Payment</div>
									<div className="font-semibold">{formatCurrency(data.requiredMinDownPaymentSar)}</div>
								</div>
							</div>
						)}
					</div>
				))}
			</div>

			{/* Joint Capacity */}
			{jointData && (
				<div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
					<h4 className="font-semibold text-gray-800 mb-3 flex items-center">
						<span className="text-blue-600 mr-2">🤝</span>
						Joint Maximum Borrowing Power
					</h4>
					<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
						<div className="bg-white p-3 rounded border text-center">
							<div className="text-xs text-gray-500">Max Property</div>
							<div className="text-xl font-bold text-blue-600">{formatCurrency(jointData.maxPropertyPrice)}</div>
						</div>
						<div className="bg-white p-3 rounded border text-center">
							<div className="text-xs text-gray-500">Max Borrowing</div>
							<div className="text-xl font-bold text-green-600">{formatCurrency(jointData.maxBorrowingAmount)}</div>
						</div>
						<div className="bg-white p-3 rounded border text-center">
							<div className="text-xs text-gray-500">Max EMI</div>
							<div className="text-xl font-bold text-purple-600">{formatCurrency(jointData.maxEmi)}</div>
						</div>
						<div className="bg-white p-3 rounded border text-center">
							<div className="text-xs text-gray-500">Max Tenor</div>
							<div className="text-xl font-bold text-orange-600">{jointData.maxTenorMonths} months</div>
						</div>
					</div>
					{jointData.calculationDetails && (
						<div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
							<div className="bg-white p-2 rounded border">
								<div className="text-gray-500">Combined Current DBR</div>
								<div className="font-semibold">{formatPercentage(jointData.calculationDetails.currentDbr * 100)}</div>
							</div>
							<div className="bg-white p-2 rounded border">
								<div className="text-gray-500">Combined New DBR</div>
								<div className="font-semibold">{formatPercentage(jointData.calculationDetails.newDbr * 100)}</div>
							</div>
							<div className="bg-white p-2 rounded border">
								<div className="text-gray-500">Max DBR</div>
								<div className="font-semibold">{formatPercentage(jointData.calculationDetails.newMaxDbr * 100)}</div>
							</div>
							<div className="bg-white p-2 rounded border">
								<div className="text-gray-500">Net Disposable Income</div>
								<div className="font-semibold">{formatCurrency(jointData.calculationDetails.netDisposableIncome)}</div>
							</div>
							<div className="bg-white p-2 rounded border">
								<div className="text-gray-500">Min Down Payment</div>
								<div className="font-semibold">{formatCurrency(jointData.requiredMinDownPaymentSar)}</div>
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

const PostPropertySelectionSection: React.FC<{ stage: any }> = ({ stage }) => {
	if (!stage?.outputResult?.data) return null;
	const data = stage.outputResult.data;

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Post-Property Selection Metrics</h3>
			<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
				<div className="text-center">
					<div className="text-xs text-gray-500 mb-1">Finance Amount</div>
					<div className="text-xl font-bold text-blue-600">{formatCurrency(data.financeAmount)}</div>
				</div>
				<div className="text-center">
					<div className="text-xs text-gray-500 mb-1">Finance + Profit</div>
					<div className="text-xl font-bold text-green-600">{formatCurrency(data.financeAmountIncludingProfit)}</div>
				</div>
				<div className="text-center">
					<div className="text-xs text-gray-500 mb-1">EMI</div>
					<div className="text-xl font-bold text-purple-600">{formatCurrency(data.emi)}</div>
				</div>
				<div className="text-center">
					<div className="text-xs text-gray-500 mb-1">LTV</div>
					<div className="text-xl font-bold text-orange-600">{data.ltv}%</div>
				</div>
			</div>
		</div>
	);
};

const PostPersonaMatchingSection: React.FC<{ stage: any }> = ({ stage }) => {
	if (!stage?.outputResult?.data) return null;
	const data = stage.outputResult.data;

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Post-Persona Matching Details</h3>
			<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
				<div className="bg-blue-50 p-3 rounded text-center">
					<div className="text-xs text-gray-600">Finance Amount</div>
					<div className="text-lg font-bold text-blue-700">{formatCurrency(data.financeAmount)}</div>
				</div>
				<div className="bg-green-50 p-3 rounded text-center">
					<div className="text-xs text-gray-600">Finance + Profit</div>
					<div className="text-lg font-bold text-green-700">{formatCurrency(data.financeAmountIncludingProfit)}</div>
				</div>
				<div className="bg-purple-50 p-3 rounded text-center">
					<div className="text-xs text-gray-600">EMI</div>
					<div className="text-lg font-bold text-purple-700">{formatCurrency(data.emi)}</div>
				</div>
				<div className="bg-orange-50 p-3 rounded text-center">
					<div className="text-xs text-gray-600">Tenor</div>
					<div className="text-lg font-bold text-orange-700">{data.tenor} months</div>
				</div>
				<div className="bg-gray-50 p-3 rounded text-center">
					<div className="text-xs text-gray-600">Total New Obligations</div>
					<div className="text-lg font-bold text-gray-700">{formatCurrency(data.totalNewFinancialObligation)}</div>
				</div>
				<div className="bg-red-50 p-3 rounded text-center">
					<div className="text-xs text-gray-600">Current DBR</div>
					<div className="text-lg font-bold text-red-700">{formatPercentage(data.currentDbr)}</div>
				</div>
				<div className="bg-yellow-50 p-3 rounded text-center">
					<div className="text-xs text-gray-600">New Combined DBR</div>
					<div className="text-lg font-bold text-yellow-700">{formatPercentage(data.newCombinedDbr)}</div>
				</div>
				<div className="bg-indigo-50 p-3 rounded text-center">
					<div className="text-xs text-gray-600">Disposable Income</div>
					<div className="text-lg font-bold text-indigo-700">{formatCurrency(data.disposableIncome)}</div>
				</div>
				<div className="bg-pink-50 p-3 rounded text-center col-span-2 md:col-span-1">
					<div className="text-xs text-gray-600">Obligations / Disposable Income</div>
					<div className="text-lg font-bold text-pink-700">{formatPercentage(data.obligationsToDisposableIncomeRatio)}</div>
				</div>
			</div>
		</div>
	);
};

const ScorecardEvaluationSection: React.FC<{ stage: any }> = ({ stage }) => {
	if (!stage?.outputResult?.data?.scorecards) return null;
	const scorecards = stage.outputResult.data.scorecards;

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Scorecard Evaluation</h3>
			<div className="space-y-6">
				{Object.entries(scorecards).map(([nationalId, data]: [string, any]) => (
					<div key={nationalId} className="border rounded-lg p-4 bg-gray-50">
						<div className="flex justify-between items-center mb-4">
							<h4 className="font-semibold text-gray-800">{nationalId}</h4>
							<div className="flex items-center space-x-2">
								<span className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
									Score: {data.score}
								</span>
								<span
									className={`text-xs font-semibold px-3 py-1 rounded-full ${
										data.riskGrade === 'Good' || data.riskGrade === 'Acceptable'
											? 'bg-green-100 text-green-800'
											: 'bg-yellow-100 text-yellow-800'
									}`}>
									Grade: {data.riskGrade}
								</span>
							</div>
						</div>

						<div className="overflow-x-auto">
							<table className="min-w-full text-xs text-left">
								<thead className="bg-gray-100 text-gray-600 uppercase font-medium">
									<tr>
										<th className="p-2 border-b">Attribute</th>
										<th className="p-2 border-b">Value</th>
										<th className="p-2 border-b text-center">Matched</th>
										<th className="p-2 border-b text-right">Points</th>
										<th className="p-2 border-b text-right">Weight</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-gray-200 bg-white">
									{data.attributes.map((attr: any, idx: number) => (
										<tr key={idx} className="hover:bg-gray-50 transition-colors">
											<td className="p-2 text-gray-700 font-medium whitespace-nowrap" title={attr.attributeName}>
												{formatStageName(attr.attributeName)}
											</td>
											<td className="p-2 text-gray-600 font-mono">
												{attr.value !== undefined ? String(attr.value) : '-'}
											</td>
											<td className="p-2 text-center">
												{attr.matched ? (
													<span className="text-green-500 font-bold">✓</span>
												) : (
													<span className="text-gray-300">-</span>
												)}
											</td>
											<td className="p-2 text-right font-semibold text-blue-600">
												{attr.factorPoints}
											</td>
											<td className="p-2 text-right text-gray-500">
												{attr.weight}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

const PersonaMatchingSection: React.FC<{ stage: any }> = ({ stage }) => {
	if (!stage?.outputResult?.data) return null;
	const { persona, input } = stage.outputResult.data;

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Persona Matching</h3>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				<div>
					<h4 className="text-sm font-semibold text-gray-600 mb-2">Input Factors</h4>
					<div className="bg-gray-50 p-3 rounded border text-sm space-y-2">
						<div className="flex justify-between">
							<span className="text-gray-500">Nationality</span>
							<span className="font-medium">{input.nationality}</span>
						</div>
						<div className="flex justify-between">
							<span className="text-gray-500">Sector</span>
							<span className="font-medium">{input.sector}</span>
						</div>
						<div className="flex justify-between">
							<span className="text-gray-500">Score</span>
							<span className="font-medium">{input.score}</span>
						</div>
					</div>
				</div>
				<div>
					<h4 className="text-sm font-semibold text-gray-600 mb-2">Matched Persona Strategy</h4>
					<div className="bg-blue-50 p-3 rounded border text-sm space-y-2">
						<div className="flex justify-between">
							<span className="text-gray-500">ROI</span>
							<span className="font-medium font-bold text-blue-700">{input.roi}%</span>
						</div>
						<div className="flex justify-between">
							<span className="text-gray-500">Max Finance</span>
							<span className="font-medium font-bold text-green-700">{formatCurrency(persona.maxFinanceAmount)}</span>
						</div>
						<div className="flex justify-between">
							<span className="text-gray-500">Approving Body</span>
							<span className="font-medium">{persona.approvingBody}</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

const KnockoutSection: React.FC<{ stage: any; application: any }> = ({ stage, application }) => {
	if (!stage?.outputResult?.data?.knockoutChecksResults) return null;
	const results = stage.outputResult.data.knockoutChecksResults;

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Knockout Checks</h3>
			<div className="space-y-4">
				{Object.entries(results).map(([nationalId, data]: [string, any]) => (
					<div key={nationalId} className="border rounded-lg p-4 bg-gray-50">
						<div className="flex justify-between items-center mb-3">
							<h4 className="font-semibold text-gray-800">{nationalId}</h4>
							<BooleanBadge value={data.isEligible} />
						</div>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-2">
							{Object.entries(data.checks).map(([checkName, check]: [string, any]) => (
								<div
									key={checkName}
									className="flex justify-between items-center p-2 bg-white rounded border text-xs">
									<span className="font-medium text-gray-700">{formatStageName(checkName)}</span>
									<div className="text-right">
										<div className={check.isEligible ? 'text-green-600' : 'text-red-600 font-bold'}>
											{typeof check.value === 'number' ? check.value.toLocaleString() : check.value}
										</div>
										<div className="text-gray-400 text-[10px]">
											{check.min !== undefined ? `Min: ${check.min}` : ''}
											{check.max !== undefined ? `Max: ${check.max}` : ''}
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

const DeviationSection: React.FC<{ stage: any }> = ({ stage }) => {
	if (!stage?.outputResult?.data) return null;
	const { deviationResults, financialDeviations } = stage.outputResult.data;

	// Check if there are any deviations to show
	const allClean =
		Object.values(deviationResults || {}).every((d: any) => !d.isRejected && d.deviationsCount === 0) &&
		(!financialDeviations || (!financialDeviations.isRejected && financialDeviations.deviationsCount === 0));

	if (allClean) return null;

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border border-orange-200">
			<h3 className="text-lg font-semibold text-orange-800 mb-4 border-b pb-2">Deviations & Exceptions</h3>
			{/* Implementation details for deviations if needed, for now just a summary */}
			<div className="text-sm text-gray-700">
				Deviations detected. Please review the raw data for specific details.
			</div>
		</div>
	);
};

const AdditionalIncomeSection: React.FC<{ stage: any; application: any }> = ({ stage, application }) => {
	const results = stage?.outputResult?.data?.additionalIncomeResults;
	if (!results) return null;

	const getApplicantName = (nationalId: string) => {
		if (application.mainApplicant.nationalId === nationalId) {
			const p = application.mainApplicant.personalInformation;
			return `${p.firstName} ${p.familyName} (Main)`;
		}
		const coApplicant = application.coApplicants.find((a: any) => a.nationalId === nationalId);
		if (coApplicant) {
			const p = coApplicant.personalInformation;
			return `${p.firstName} ${p.familyName} (Co-Applicant)`;
		}
		return nationalId;
	};

	const renderPrivateBusinessMetadata = (metadata: any) => {
		if (!metadata) return null;
		return (
			<div className="space-y-3">
				<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
					<div className="bg-white p-2 rounded border text-xs">
						<div className="text-gray-500">Total Transactions Analyzed</div>
						<div className="font-semibold text-gray-800">{metadata.totalTransactionsAnalyzed?.toLocaleString()}</div>
					</div>
					<div className="bg-white p-2 rounded border text-xs">
						<div className="text-gray-500">Business Credit Count</div>
						<div className="font-semibold text-gray-800">{metadata.businessCreditCount?.toLocaleString()}</div>
					</div>
					<div className="bg-white p-2 rounded border text-xs">
						<div className="text-gray-500">Total Business Income</div>
						<div className="font-semibold text-green-700">{formatCurrency(metadata.totalBusinessIncome || 0)}</div>
					</div>
					<div className="bg-white p-2 rounded border text-xs">
						<div className="text-gray-500">Transaction Months</div>
						<div className="font-semibold text-gray-800">{Math.ceil(metadata.numOfTransactionMonths || 0)}</div>
					</div>
				</div>
				{metadata.matchedCategories && (
					<details className="bg-white rounded border p-2 text-xs">
						<summary className="cursor-pointer font-medium text-gray-700">
							Matched Categories ({Object.keys(metadata.matchedCategories).length} types)
						</summary>
						<div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
							{Object.entries(metadata.matchedCategories).map(([category, txnIds]: [string, any]) => (
								<div key={category} className="bg-gray-50 p-2 rounded border">
									<div className="font-medium text-gray-700 capitalize mb-1">
										{formatStageName(category)} ({Array.isArray(txnIds) ? txnIds.length : 0} transactions)
									</div>
								</div>
							))}
						</div>
					</details>
				)}
				{metadata.transactionIds && metadata.transactionIds.length > 0 && (
					<div className="text-xs text-gray-500">
						Matching Transaction IDs: {metadata.transactionIds.length}
					</div>
				)}
			</div>
		);
	};

	const renderPensionMetadata = (metadata: any) => {
		if (!metadata) return null;
		return (
			<div className="space-y-3">
				<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
					<div className="bg-white p-2 rounded border text-xs">
						<div className="text-gray-500">National ID</div>
						<div className="font-semibold font-mono text-gray-800">{metadata.nationalId}</div>
					</div>
					<div className="bg-white p-2 rounded border text-xs">
						<div className="text-gray-500">API Success</div>
						<BooleanBadge value={metadata.apiSuccess} />
					</div>
					<div className="bg-white p-2 rounded border text-xs">
						<div className="text-gray-500">Total Pension Options</div>
						<div className="font-semibold text-gray-800">{metadata.totalPensionOptions}</div>
					</div>
				</div>
				{metadata.selectedPension && (
					<div className="bg-blue-50 p-3 rounded border border-blue-200">
						<h6 className="text-xs font-semibold text-blue-800 mb-2">Selected Pension</h6>
						<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
							<div className="text-xs">
								<div className="text-gray-500">Retirement Date</div>
								<div className="font-semibold text-gray-800">{metadata.selectedPension.retirementDate}</div>
							</div>
							<div className="text-xs">
								<div className="text-gray-500">Total Pension Amount</div>
								<div className="font-semibold text-green-700">{formatCurrency(metadata.selectedPension.totalPensionAmount || 0)}</div>
							</div>
							<div className="text-xs">
								<div className="text-gray-500">Age</div>
								<div className="font-semibold text-gray-800">{metadata.selectedPension.age}</div>
							</div>
							<div className="text-xs">
								<div className="text-gray-500">Retirement Type</div>
								<div className="font-semibold text-gray-800">{metadata.selectedPension.retirementType}</div>
							</div>
						</div>
					</div>
				)}
			</div>
		);
	};

	const renderIncentivesMetadata = (metadata: any) => {
		if (!metadata) return null;
		return (
			<div className="grid grid-cols-2 md:grid-cols-3 gap-3">
				<div className="bg-white p-2 rounded border text-xs">
					<div className="text-gray-500">Total Transactions Analyzed</div>
					<div className="font-semibold text-gray-800">{metadata.totalTransactionsAnalyzed?.toLocaleString()}</div>
				</div>
				<div className="bg-white p-2 rounded border text-xs">
					<div className="text-gray-500">Incentive Transaction Count</div>
					<div className="font-semibold text-gray-800">{metadata.incentiveTransactionCount?.toLocaleString()}</div>
				</div>
				<div className="bg-white p-2 rounded border text-xs">
					<div className="text-gray-500">Total Incentives</div>
					<div className="font-semibold text-green-700">{formatCurrency(metadata.totalIncentives || 0)}</div>
				</div>
				<div className="bg-white p-2 rounded border text-xs">
					<div className="text-gray-500">Transaction Months</div>
					<div className="font-semibold text-gray-800">{metadata.numOfTransactionMonths?.toFixed(2)}</div>
				</div>
				<div className="bg-white p-2 rounded border text-xs">
					<div className="text-gray-500">Known Base Salary</div>
					<div className="font-semibold text-blue-700">{formatCurrency(metadata.knownBaseSalaryAmount || 0)}</div>
				</div>
				{metadata.transactionIds && metadata.transactionIds.length > 0 && (
					<div className="bg-white p-2 rounded border text-xs">
						<div className="text-gray-500">Matching Transactions</div>
						<div className="font-semibold text-gray-800">{metadata.transactionIds.length}</div>
					</div>
				)}
			</div>
		);
	};

	const renderRentalMetadata = (metadata: any) => {
		if (!metadata) return null;
		return (
			<div className="grid grid-cols-2 md:grid-cols-3 gap-3">
				<div className="bg-white p-2 rounded border text-xs">
					<div className="text-gray-500">Total Credits Analyzed</div>
					<div className="font-semibold text-gray-800">{metadata.totalCreditsAnalyzed?.toLocaleString()}</div>
				</div>
				<div className="bg-white p-2 rounded border text-xs">
					<div className="text-gray-500">Amount Groups Found</div>
					<div className="font-semibold text-gray-800">{metadata.amountGroupsFound?.toLocaleString()}</div>
				</div>
				<div className="bg-white p-2 rounded border text-xs">
					<div className="text-gray-500">Recurring Groups Identified</div>
					<div className="font-semibold text-gray-800">{metadata.recurringGroupsIdentified?.toLocaleString()}</div>
				</div>
				<div className="bg-white p-2 rounded border text-xs">
					<div className="text-gray-500">Recurring Transaction Count</div>
					<div className="font-semibold text-gray-800">{metadata.recurringTransactionCount?.toLocaleString()}</div>
				</div>
				{metadata.transactionIds && metadata.transactionIds.length > 0 && (
					<div className="bg-white p-2 rounded border text-xs">
						<div className="text-gray-500">Matching Transactions</div>
						<div className="font-semibold text-gray-800">{metadata.transactionIds.length}</div>
					</div>
				)}
			</div>
		);
	};

	const renderMetadata = (type: string, metadata: any) => {
		switch (type) {
			case 'PRIVATE_BUSINESS':
				return renderPrivateBusinessMetadata(metadata);
			case 'PENSION':
				return renderPensionMetadata(metadata);
			case 'INCENTIVES':
				return renderIncentivesMetadata(metadata);
			case 'RENTAL':
				return renderRentalMetadata(metadata);
			case 'SECOND_JOB':
				return null; // SECOND_JOB has no metadata fields beyond type
			default:
				return metadata ? (
					<details className="bg-white rounded border p-2 text-xs">
						<summary className="cursor-pointer font-medium text-gray-700">Raw Metadata</summary>
						<pre className="mt-2 text-[11px] overflow-auto">{JSON.stringify(metadata, null, 2)}</pre>
					</details>
				) : null;
		}
	};

	const getTypeColor = (type: string) => {
		const colors: Record<string, string> = {
			PRIVATE_BUSINESS: 'bg-purple-100 text-purple-800',
			PENSION: 'bg-blue-100 text-blue-800',
			INCENTIVES: 'bg-yellow-100 text-yellow-800',
			RENTAL: 'bg-teal-100 text-teal-800',
			SECOND_JOB: 'bg-indigo-100 text-indigo-800'
		};
		return colors[type] || 'bg-gray-100 text-gray-800';
	};

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Additional Income</h3>
			<div className="space-y-4">
				{Object.entries(results).map(([nationalId, data]: [string, any]) => (
					<div key={nationalId} className="border rounded-lg p-4 bg-gray-50">
						<div className="flex items-center justify-between mb-3">
							<h4 className="font-semibold text-gray-800">{getApplicantName(nationalId)}</h4>
							<div className="flex items-center gap-2">
								<span className={`px-2 py-1 rounded-full text-xs font-semibold ${getTypeColor(data.type)}`}>
									{formatStageName(data.type)}
								</span>
							</div>
						</div>

						{/* Value display */}
						{data.value !== undefined && (
							<div className="mb-3">
								<div className="bg-white p-3 rounded border text-center inline-block min-w-[200px]">
									<div className="text-xs text-gray-500">Additional Income Value</div>
									<div className={`text-xl font-bold ${data.value > 0 ? 'text-green-600' : 'text-gray-500'}`}>
										{formatCurrency(data.value)}
									</div>
								</div>
							</div>
						)}

						{/* Type-specific metadata */}
						{data.metadata && renderMetadata(data.type, data.metadata)}
					</div>
				))}
			</div>

			{/* Rejection reasons if any */}
			{stage?.outputResult?.rejectionReasons && stage.outputResult.rejectionReasons.length > 0 && (
				<div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
					<h4 className="text-sm font-semibold text-red-800 mb-2">Rejection Reasons</h4>
					<ul className="space-y-1 text-sm text-red-700">
						{stage.outputResult.rejectionReasons.map((reason: any, idx: number) => (
							<li key={idx} className="flex items-start">
								<span className="mr-2 text-red-400">•</span>
								<span>{typeof reason === 'object' ? reason.message : reason}</span>
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
};

const InvestmentPortfolioSection: React.FC<{ stage: any }> = ({ stage }) => {
	if (!stage?.outputResult?.data) return null;
	const { totalMatches, eligibleMatches, matchedPersonaRule, selectedPortfolioId, portfolios } = stage.outputResult.data;

	const selectedPortfolio = portfolios?.find((p: any) => p.portfolioId === selectedPortfolioId);

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Investment Portfolio</h3>

			{/* Summary */}
			<div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
				<div className="bg-blue-50 p-3 rounded text-center">
					<div className="text-xs text-gray-500 mb-1">Total Matches</div>
					<div className="text-lg font-bold text-blue-700">{totalMatches}</div>
				</div>
				<div className="bg-green-50 p-3 rounded text-center">
					<div className="text-xs text-gray-500 mb-1">Eligible Matches</div>
					<div className="text-lg font-bold text-green-700">{eligibleMatches}</div>
				</div>
				<div className="bg-purple-50 p-3 rounded text-center">
					<div className="text-xs text-gray-500 mb-1">Selected Portfolio</div>
					<div className="text-xs font-semibold text-purple-700">{selectedPortfolio?.name || 'N/A'}</div>
				</div>
				<div className="bg-amber-50 p-3 rounded text-center">
					<div className="text-xs text-gray-500 mb-1">Portfolio Status</div>
					<div className={`text-xs font-bold ${selectedPortfolio?.status === 'PUBLISHED' ? 'text-green-700' : 'text-amber-700'}`}>
						{selectedPortfolio?.status || 'N/A'}
					</div>
				</div>
			</div>

			{/* Matched Persona Rule */}
			{matchedPersonaRule && (
				<div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
					<h4 className="text-sm font-semibold text-blue-800 mb-3 border-b border-blue-200 pb-2">
						Matched Persona Rule
					</h4>
					<div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
						<div>
							<div className="text-gray-500 text-xs">Persona Name</div>
							<div className="font-semibold text-gray-800">{matchedPersonaRule.personaName}</div>
						</div>
						<div>
							<div className="text-gray-500 text-xs">Credit Rating</div>
							<div className="font-semibold text-gray-800">{matchedPersonaRule.creditRating}</div>
						</div>
						<div>
							<div className="text-gray-500 text-xs">Subsidy Type</div>
							<div className="font-semibold text-gray-800">{formatStageName(matchedPersonaRule.subsidyType)}</div>
						</div>
						<div>
							<div className="text-gray-500 text-xs">Contract Type</div>
							<div className="font-semibold text-gray-800">{formatStageName(matchedPersonaRule.defaultContractType)}</div>
						</div>
						<div>
							<div className="text-gray-500 text-xs">Effective Annual Rate</div>
							<div className="font-semibold text-green-700">{matchedPersonaRule.effectiveAnnualFlatRatePercent}%</div>
						</div>
						<div>
							<div className="text-gray-500 text-xs">Snapshotted Rate</div>
							<div className="font-semibold text-gray-700">{matchedPersonaRule.snapshottedAnnualFlatRatePercent}%</div>
						</div>
						<div>
							<div className="text-gray-500 text-xs">Min Finance Amount</div>
							<div className="font-semibold text-gray-800">{formatCurrency(parseFloat(matchedPersonaRule.minFinanceAmount))}</div>
						</div>
						<div>
							<div className="text-gray-500 text-xs">Max Finance Amount</div>
							<div className="font-semibold text-gray-800">{formatCurrency(parseFloat(matchedPersonaRule.maxFinanceAmount))}</div>
						</div>
						{matchedPersonaRule.isOverridden && (
							<div className="col-span-2 md:col-span-4">
								<div className="bg-amber-100 border border-amber-300 rounded px-2 py-1 inline-flex items-center">
									<span className="text-amber-700 text-xs font-medium">⚠️ Rate Overridden</span>
								</div>
							</div>
						)}
					</div>
				</div>
			)}

			{/* Selected Portfolio Details */}
			{selectedPortfolio && (
				<div className="mb-6 bg-gray-50 border rounded-lg p-4">
					<h4 className="text-sm font-semibold text-gray-800 mb-3 border-b pb-2">Selected Portfolio Details</h4>
					<div className="space-y-3">
						<div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
							<div>
								<div className="text-gray-500 text-xs">Portfolio Name</div>
								<div className="font-semibold text-gray-800">{selectedPortfolio.name}</div>
							</div>
							<div>
								<div className="text-gray-500 text-xs">Status</div>
								<div className={`font-semibold ${selectedPortfolio.status === 'PUBLISHED' ? 'text-green-700' : 'text-amber-700'}`}>
									{selectedPortfolio.status}
								</div>
							</div>
							<div>
								<div className="text-gray-500 text-xs">Min Investment</div>
								<div className="font-semibold text-gray-800">{formatCurrency(parseFloat(selectedPortfolio.minInvestmentAmount))}</div>
							</div>
						</div>

						{/* Matched Sector Config */}
						{selectedPortfolio.matchedSectorConfig && (
							<div className="bg-white border rounded p-3">
								<div className="text-xs font-semibold text-gray-600 mb-2">Matched Sector Configuration</div>
								<div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
									<div>
										<div className="text-gray-500">Sector Name</div>
										<div className="font-semibold text-gray-800">{selectedPortfolio.matchedSectorConfig.sectorName}</div>
									</div>
									{selectedPortfolio.matchedSectorConfig.allocationPercent && (
										<div>
											<div className="text-gray-500">Allocation Percent</div>
											<div className="font-semibold text-blue-700">{selectedPortfolio.matchedSectorConfig.allocationPercent}%</div>
										</div>
									)}
									{selectedPortfolio.matchedSectorConfig.riskLevels?.length > 0 && (
										<div>
											<div className="text-gray-500">Risk Levels</div>
											<div className="font-semibold text-gray-800">{selectedPortfolio.matchedSectorConfig.riskLevels.join(', ')}</div>
										</div>
									)}
								</div>
							</div>
						)}

						{/* Product Types & Contract Types */}
						<div className="grid grid-cols-2 gap-3">
							{selectedPortfolio.productTypes?.length > 0 && (
								<div className="bg-white border rounded p-2">
									<div className="text-xs text-gray-500 mb-1">Product Types</div>
									<div className="flex flex-wrap gap-1">
										{selectedPortfolio.productTypes.map((type: string, idx: number) => (
											<span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
												{formatStageName(type)}
											</span>
										))}
									</div>
								</div>
							)}
							{selectedPortfolio.contractTypes?.length > 0 && (
								<div className="bg-white border rounded p-2">
									<div className="text-xs text-gray-500 mb-1">Contract Types</div>
									<div className="flex flex-wrap gap-1">
										{selectedPortfolio.contractTypes.map((type: string, idx: number) => (
											<span key={idx} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
												{formatStageName(type)}
											</span>
										))}
									</div>
								</div>
							)}
						</div>

						{/* Time Window */}
						{selectedPortfolio.timeWindow && (
							<div className="bg-white border rounded p-2">
								<div className="text-xs text-gray-500 mb-1">Time Window</div>
								<div className="text-xs text-gray-800">
									<span className="font-medium">Starts:</span> {new Date(selectedPortfolio.timeWindow.startsAt).toLocaleString()}
									<span className="mx-2">•</span>
									<span className="font-medium">Ends:</span> {new Date(selectedPortfolio.timeWindow.endsAt).toLocaleString()}
								</div>
							</div>
						)}
					</div>
				</div>
			)}

			{/* All Portfolios */}
			{portfolios && portfolios.length > 0 && (
				<details className="bg-gray-50 border rounded-lg p-4">
					<summary className="cursor-pointer font-semibold text-gray-700 hover:text-gray-900">
						All Matched Portfolios ({portfolios.length})
					</summary>
					<div className="mt-4 space-y-3">
						{portfolios.map((portfolio: any, idx: number) => (
							<div
								key={portfolio.portfolioId}
								className={`border rounded-lg p-3 ${
									portfolio.portfolioId === selectedPortfolioId
										? 'bg-blue-50 border-blue-300'
										: 'bg-white border-gray-200'
								}`}>
								<div className="flex items-start justify-between mb-2">
									<div className="flex-1">
										<div className="font-semibold text-sm text-gray-800 mb-1">
											{portfolio.portfolioId === selectedPortfolioId && (
												<span className="text-blue-600 mr-1">★</span>
											)}
											{portfolio.name}
										</div>
										<div className="text-xs text-gray-600">
											{portfolio.matchedSectorConfig.sectorName}
										</div>
									</div>
									<div className={`text-xs px-2 py-1 rounded font-medium ${
										portfolio.status === 'PUBLISHED' 
											? 'bg-green-100 text-green-700' 
											: 'bg-amber-100 text-amber-700'
									}`}>
										{portfolio.status}
									</div>
								</div>
								<div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
									<div>
										<div className="text-gray-500">Min Investment</div>
										<div className="font-medium text-gray-800">{formatCurrency(parseFloat(portfolio.minInvestmentAmount))}</div>
									</div>
									{portfolio.matchedSectorConfig.allocationPercent && (
										<div>
											<div className="text-gray-500">Allocation</div>
											<div className="font-medium text-gray-800">{portfolio.matchedSectorConfig.allocationPercent}%</div>
										</div>
									)}
									<div>
										<div className="text-gray-500">Product Types</div>
										<div className="font-medium text-gray-800">{portfolio.productTypes?.length || 0}</div>
									</div>
									<div>
										<div className="text-gray-500">Persona Rules</div>
										<div className="font-medium text-gray-800">{portfolio.personaRules?.length || 0}</div>
									</div>
								</div>
							</div>
						))}
					</div>
				</details>
			)}
		</div>
	);
};

const FinalDecisionSection: React.FC<{ stage: any; application: JointApplicationResponse['application'] }> = ({ stage, application }) => {
	const finalDecision = stage?.outputResult?.data;
	const status = finalDecision?.status || application.status;

	const applicantsMap = React.useMemo(() => {
		const map = new Map<string, string>();
		if (application?.mainApplicant?.nationalId) {
			map.set(
				application.mainApplicant.nationalId,
				`${application.mainApplicant.personalInformation.firstName} ${application.mainApplicant.personalInformation.familyName}`
			);
		}
		if (application?.coApplicants) {
			application.coApplicants.forEach((co) => {
				if (co.nationalId) {
					map.set(
						co.nationalId,
						`${co.personalInformation.firstName} ${co.personalInformation.familyName}`
					);
				}
			});
		}
		return map;
	}, [application]);

	return (
		<div className="bg-white p-6 rounded-lg shadow-md border-2 border-gray-200">
			<div className="flex items-center justify-between mb-6">
				<h3 className="text-xl font-semibold text-gray-800">Final Decision</h3>
				<div className={`px-4 py-2 rounded-lg font-semibold text-sm ${getStatusBadgeColor(status)}`}>
					{formatStageName(status)}
				</div>
			</div>

            
			{/* Rejection Details */}
			{status === 'REJECTED' && finalDecision && (
				<div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
					<h3 className="text-sm font-medium text-red-800 mb-4">Rejection Reasons</h3>
					
					{/* Group reasons by applicant */}
					{(() => {
						const reasonsByApplicant = new Map<string, any[]>();
						const generalReasons: any[] = [];

						finalDecision.rejectionReasons?.forEach((reason: any) => {
							const isObject = typeof reason === 'object' && reason !== null;
							const applicantId = isObject ? reason.applicantId : null;
							
							if (applicantId && applicantsMap.has(applicantId)) {
								if (!reasonsByApplicant.has(applicantId)) {
									reasonsByApplicant.set(applicantId, []);
								}
								reasonsByApplicant.get(applicantId)?.push(reason);
							} else {
								generalReasons.push(reason);
							}
						});

						return (
							<div className="space-y-4">
								{/* Display per-applicant reasons */}
								{Array.from(reasonsByApplicant.entries()).map(([applicantId, reasons]) => (
									<div key={applicantId} className="bg-white p-3 rounded border border-red-100">
										<h4 className="text-sm font-semibold text-gray-800 mb-2 border-b border-gray-100 pb-1">
											{applicantsMap.get(applicantId)}
										</h4>
										<ul className="space-y-1 text-sm text-red-700 ml-2">
											{reasons.map((reason: any, idx: number) => {
												const isObject = typeof reason === 'object' && reason !== null;
												return (
													<li key={idx} className="flex flex-col items-start mb-1">
														<div className="flex items-start">
															<span className="mr-2 text-red-400">•</span>
															<span>
																{isObject ? reason.message : reason}
																{isObject && reason.code && (
																	<span className="text-xs text-red-500 ml-1 font-mono bg-red-50 px-1 rounded">
																		{reason.code}
																	</span>
																)}
															</span>
														</div>
														{isObject && reason.daysBeforeReapply > 0 && (
															<span className="text-xs text-gray-500 ml-5">
																Can reapply after {reason.daysBeforeReapply} days
															</span>
														)}
													</li>
												);
											})}
										</ul>
									</div>
								))}

								{/* Display general reasons */}
								{generalReasons.length > 0 && (
									<div className="bg-white p-3 rounded border border-red-100">
										<h4 className="text-sm font-semibold text-gray-800 mb-2 border-b border-gray-100 pb-1">
											General Reasons
										</h4>
										<ul className="space-y-1 text-sm text-red-700 ml-2">
											{generalReasons.map((reason: any, idx: number) => {
												const isObject = typeof reason === 'object' && reason !== null;
												return (
													<li key={idx} className="flex flex-col items-start mb-1">
														<div className="flex items-start">
															<span className="mr-2 text-red-400">•</span>
															<span>
																{isObject ? reason.message : reason}
																{isObject && reason.code && (
																	<span className="text-xs text-red-500 ml-1 font-mono bg-red-50 px-1 rounded">
																		{reason.code}
																	</span>
																)}
															</span>
														</div>
														{isObject && reason.daysBeforeReapply > 0 && (
															<span className="text-xs text-gray-500 ml-5">
																Can reapply after {reason.daysBeforeReapply} days
															</span>
														)}
													</li>
												);
											})}
										</ul>
									</div>
								)}

								{/* Fallback if no reasons found */}
								{reasonsByApplicant.size === 0 && generalReasons.length === 0 && (
									<div className="text-sm text-red-700 pl-2">
										Application was rejected based on policy rules.
									</div>
								)}
							</div>
						);
					})()}
				</div>
			)}

			{/* Salvaging Section */}
            {finalDecision?.salvaging && (
                <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-yellow-800 mb-2 flex items-center">
                        <span className="mr-2">🛟</span> Salvaging Opportunity
                    </h3>
                    <div className="mb-2">
                        <span className="font-semibold text-yellow-900">Flag:</span>
                        <span className="ml-2 text-yellow-800 font-mono">{finalDecision.salvaging.flag}</span>
                    </div>
                    {finalDecision.salvaging.actions && finalDecision.salvaging.actions.length > 0 && (
                        <div>
                            <span className="font-semibold text-yellow-900">Recommended Actions:</span>
                            <ul className="list-disc ml-6 mt-1 text-yellow-900">
                                {finalDecision.salvaging.actions.map((action: string, idx: number) => (
                                    <li key={idx}>{action}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

			{/* Approval Details */}
			{status.includes('APPROVED') && finalDecision && (
				<div className="bg-green-50 border border-green-200 rounded-lg p-4">
					<h4 className="text-sm font-semibold text-green-800 mb-3 uppercase">Approved Offer</h4>
					<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
						<div className="text-center bg-white p-3 rounded border border-green-100">
							<div className="text-xs text-gray-500 mb-1">Finance Amount</div>
							<div className="text-lg font-bold text-green-700">{formatCurrency(finalDecision.financeAmount)}</div>
						</div>
						<div className="text-center bg-white p-3 rounded border border-green-100">
							<div className="text-xs text-gray-500 mb-1">Total (+Profit)</div>
							<div className="text-lg font-bold text-green-700">
								{formatCurrency(finalDecision.financeAmountPlusProfit)}
							</div>
						</div>
						<div className="text-center bg-white p-3 rounded border border-green-100">
							<div className="text-xs text-gray-500 mb-1">Monthly EMI</div>
							<div className="text-lg font-bold text-green-700">{formatCurrency(finalDecision.emi)}</div>
						</div>
						<div className="text-center bg-white p-3 rounded border border-green-100">
							<div className="text-xs text-gray-500 mb-1">Tenor</div>
							<div className="text-lg font-bold text-green-700">{finalDecision.tenor} months</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

const ResultsDisplay: React.FC<{ result: JointApplicationResponse }> = ({ result }) => {
	const application = result.application;
	const stages = result.stages || {};

	return (
		<div className="space-y-6">
			{/* 1. Final Decision (Top Priority) */}
			{/* 1. Final Decision (Top Priority) */}
			<FinalDecisionSection stage={stages.FINAL_DECISION} application={application} />

			{/* 2. Decision Summary Grid */}
			{Object.keys(stages).length > 0 && (
				<div className="bg-white p-6 rounded-lg shadow-sm border">
					<h4 className="text-sm font-semibold text-gray-500 uppercase mb-3">Decision Engine Flow</h4>
					<div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
						{Object.entries(stages).map(([name, stage]: [string, any]) => (
							<div key={name} className="border rounded-lg p-3 flex flex-col items-center text-center">
								<div
									className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${getStatusColor(
										stage.status
									)}`}>
									{stage.status === 'PASSED' ? '✓' : stage.status === 'FAILED' ? '✗' : '?'}
								</div>
								<h4 className="font-medium text-[10px] text-gray-800 leading-tight">{formatStageName(name)}</h4>
							</div>
						))}
					</div>
				</div>
			)}

			{/* 3. Applicant Profiles (Reference) */}
			<div className="bg-white p-6 rounded-lg shadow-sm border">
				<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Applicant Profiles</h3>
				<div className="space-y-4">
					<ApplicantProfileSection applicant={application.mainApplicant} title="Main Applicant" />
					{application.coApplicants.map((coApp: any, idx: number) => (
						<ApplicantProfileSection key={idx} applicant={coApp} title={`Co-Applicant ${idx + 1}`} />
					))}
				</div>
			</div>


			{/* 4. RAC Policy */}
			{stages.RAC_POLICY && <RACPolicySection stage={stages.RAC_POLICY} />}

			{/* 4b. Conditional Approval */}
			{stages.CONDITIONAL_APPROVAL && <ConditionalApprovalSection stage={stages.CONDITIONAL_APPROVAL} />}

			{/* 5. Credit Bureau Analysis */}
			{stages.CREDIT_BUREAU && <CreditBureauDetailedAnalysis stage={stages.CREDIT_BUREAU} application={application} />}

			{/* 5b. Open Banking */}
            {stages.OPEN_BANKING && <OpenBankingSection stage={stages.OPEN_BANKING} application={application} />}

			{/* 5b2. Additional Income */}
			{stages.ADDITIONAL_INCOME && <AdditionalIncomeSection stage={stages.ADDITIONAL_INCOME} application={application} />}

			{/* 5c. Non-Salaried RAC */}
			{stages.NON_SALARIED_RAC && (
				<NonSalariedRacSection stage={stages.NON_SALARIED_RAC} application={application} />
			)}

			{/* 6. Scorecard Evaluation */}
			{stages.SCORECARD_EVALUATION && <ScorecardEvaluationSection stage={stages.SCORECARD_EVALUATION} />}

			{/* 7. Responsible Lending */}
			{stages.RESPONSIBLE_LENDING && (
				<ResponsibleLendingSection stage={stages.RESPONSIBLE_LENDING} application={application} />
			)}

			{/* 8. Maximum Borrowing Power */}
			{stages.MAXIMUM_BORROWING_POWER && (
				<MaximumBorrowingPowerSection stage={stages.MAXIMUM_BORROWING_POWER} application={application} />
			)}

			{/* 9. Post-Property Selection */}
			{stages.POST_PROPERTY_SELECTION && <PostPropertySelectionSection stage={stages.POST_PROPERTY_SELECTION} />}

			{/* 10. Persona Matching */}
			{stages.PERSONA_MATCHING && <PersonaMatchingSection stage={stages.PERSONA_MATCHING} />}

			{/* 11. Post-Persona Matching */}
			{stages.POST_PERSONA_MATCHING && <PostPersonaMatchingSection stage={stages.POST_PERSONA_MATCHING} />}

			{/* 12. Knockout Checks */}
			{stages.KNOCKOUT && <KnockoutSection stage={stages.KNOCKOUT} application={application} />}

			{/* 13. Deviations */}
			{stages.DEVIATION && <DeviationSection stage={stages.DEVIATION} />}

			{/* 14. Investment Portfolio */}
			{stages.INVESTMENT_PORTFOLIO && <InvestmentPortfolioSection stage={stages.INVESTMENT_PORTFOLIO} />}
		</div>
	);
};

// ==================== MAIN COMPONENT ====================
interface JointApplicationFormProps {
	sectors: Array<{ id: string; name: string }>;
	baseUrl: string;
}

const JointApplicationForm: React.FC<JointApplicationFormProps> = ({ sectors, baseUrl }) => {
	const [mainApplicant, setMainApplicant] = useState<ApplicantData>(createInitialApplicant(1));
	const [coApplicants, setCoApplicants] = useState<ApplicantData[]>([]);
	const [property, setProperty] = useState<Property>(INITIAL_PROPERTY);
	const [requestedFinancialParameters, setRequestedFinancialParameters] = useState<RequestedFinancialParameters>(INITIAL_REQUESTED_FINANCIAL_PARAMETERS);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');
	const [result, setResult] = useState<JointApplicationResponse | null>(null);
	const [applicationId, setApplicationId] = useState('');
	const [isPolling, setIsPolling] = useState(false);

	const addCoApplicant = () => {
		setCoApplicants([...coApplicants, createInitialApplicant(coApplicants.length + 2)]);
	};

	const removeCoApplicant = (index: number) => {
		setCoApplicants(coApplicants.filter((_, i) => i !== index));
	};

	const updateCoApplicant = (index: number, updatedApplicant: ApplicantData) => {
		const updated = [...coApplicants];
		updated[index] = updatedApplicant;
		setCoApplicants(updated);
	};

	const pollApplicationStatus = useCallback(
		async (appId: string) => {
			try {
				const response = await fetch(`${baseUrl}/api/v1/application/get-application/${appId}`);
				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}
				const data = await response.json();
				if (data.success && data.data) {
					const applicationData: JointApplicationResponse = data.data;
					setResult(applicationData);
					// Normalized behavior: once we successfully fetch and render application status,
					// stop polling and re-enable the submit button so new applications can be submitted.
					setIsPolling(false);
				}
			} catch (error) {
				console.error('Error polling application status:', error);
			}
		},
		[baseUrl]
	);

	useEffect(() => {
		if (!isPolling || !applicationId) return;
		const interval = setInterval(() => pollApplicationStatus(applicationId), 2000);
		return () => clearInterval(interval);
	}, [isPolling, applicationId, pollApplicationStatus]);

	const handleSubmit = async () => {
		// Validation
		if (!mainApplicant.nationalId) {
			setError('Please enter national ID for the main applicant');
			return;
		}

		setIsLoading(true);
		setError('');
		setResult(null);
		setApplicationId('');
		setIsPolling(false);

		try {
			const payload: JointApplicationData = {
				mainApplicant,
				coApplicants,
				property,
				requestedFinancialParameters
			};

			console.log(`${baseUrl}/api/v1/application/loan`)
			const response = await fetch(`${baseUrl}/api/v1/application/loan`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(payload)
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const data = await response.json();
			if (data.success && data.data && data.data.applicationId) {
				// Start polling with the application ID
				setApplicationId(data.data.applicationId);
				setIsPolling(true);
			} else {
				throw new Error(data.message || 'Failed to submit application');
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to submit application');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="space-y-6">
			{/* Main Applicant */}
			<ApplicantForm
				applicant={mainApplicant}
				onChange={setMainApplicant}
				title="Main Applicant"
			/>

			{/* Co-Applicants */}
			{coApplicants.map((coApplicant, index) => (
				<ApplicantForm
					key={index}
					applicant={coApplicant}
					onChange={(updated) => updateCoApplicant(index, updated)}
					onRemove={() => removeCoApplicant(index)}
					title={`Co-Applicant ${index + 1}`}
				/>
			))}

			{/* Add Co-Applicant Button */}
			<div className="flex justify-center">
				<button
					onClick={addCoApplicant}
					className="bg-green-600 text-white py-2 px-6 rounded-lg font-medium hover:bg-green-700 transition-colors shadow-sm">
					+ Add Co-Applicant
				</button>
			</div>

			{/* Property */}
			<PropertyForm property={property} onChange={setProperty} />

			{/* Requested Financial Parameters */}
			<RequestedFinancialParametersForm
				params={requestedFinancialParameters}
				onChange={setRequestedFinancialParameters}
			/>

			{/* Submit Button */}
			<div className="flex justify-center flex-col items-center gap-4">
				{error && <p className="text-red-600 text-sm font-medium">{error}</p>}
				{isPolling && applicationId && (
					<div className="text-center">
						<p className="text-gray-600 text-sm mb-1">Application ID: <span className="font-mono font-semibold">{applicationId}</span></p>
						<p className="text-blue-600 text-sm animate-pulse">Processing application...</p>
					</div>
				)}
				<button
					onClick={handleSubmit}
					disabled={isLoading || isPolling}
					className="bg-blue-600 text-white py-3 px-12 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-blue-400 transition-colors shadow-md">
					{isLoading ? 'Submitting...' : isPolling ? 'Processing...' : 'Submit Application'}
				</button>
			</div>

			{/* Results */}
			{result && (
				<div className="mt-8 border-t-2 pt-6">
					<ResultsDisplay result={result} />
					
					{/* Raw JSON */}
					<div className="mt-6">
						<details className="bg-gray-100 rounded-lg p-4">
							<summary className="cursor-pointer font-medium text-gray-700">View Raw JSON Response</summary>
							<pre className="mt-4 text-xs overflow-auto max-h-96 bg-white p-4 rounded border">
								{JSON.stringify(result, null, 2)}
							</pre>
						</details>
					</div>
				</div>
			)}
		</div>
	);
};

export default JointApplicationForm;
